import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { useRef } from 'react';
import { useOverlayContext } from 'seagull';
import { useLocaleKeys } from '~/i18n';
import { handleRequest } from '~/infra/utils';
import { SOMETHING_ELSE_OPTION_ID } from '~/models';
import { useAgentConversation } from './useAgentConversation';
import { REGREET_DELAY_MS, useGreetingRefresh } from './useGreetingRefresh';
export const useBulkAgentPane = (param)=>{
    let { api, createConversation, restoreMostRecent, onScopeRestored, sendFeedback, onAssistantMessage, onHydrated, onEntityApplied, onUndoUnavailable, onSellerPrompted, isOpen, feedbackToastTestId, greetingScopeKey, regreetDelayMs = REGREET_DELAY_MS } = param;
    const { t } = useLocaleKeys();
    const { showErrorToast, showSuccessToast } = useOverlayContext();
    const conversationIdRef = useRef(null);
    const createInFlightRef = useRef(null);
    const clearEpochRef = useRef(0);
    const lastInstructionRef = useRef('');
    const conversation = useAgentConversation(_object_spread_props(_object_spread({
        api,
        ensureConversationId,
        getConversationId,
        resetConversation,
        initialConversationId: null,
        isOpen,
        enrichableTypes: [
            'bulk-applied'
        ],
        clearingLabel: t.ai.drafting.body.clearing(),
        canClearWithoutUserMessage: true,
        resetEndsTurn: true,
        onHydrated,
        onAssistantMessage
    }, onEntityApplied != null ? {
        onDraftApplied: onEntityApplied
    } : {}), {
        onError: (reason)=>{
            if (reason === 'hydrate') showErrorToast(t.ai.drafting.errors.hydrate());
            else if (reason === 'clear') showErrorToast(t.ai.drafting.errors.clear());
            else if (reason === 'undoUnavailable') onUndoUnavailable === null || onUndoUnavailable === void 0 ? void 0 : onUndoUnavailable();
        }
    }));
    const { markGreeted, forgetGreeting } = useGreetingRefresh({
        greetingScopeKey,
        regreetDelayMs,
        canRegreet: isRegreetAllowed(),
        createConversation,
        adoptGreeting: (conversationId, message)=>{
            conversationIdRef.current = conversationId;
            conversation.hydrate([
                message
            ]);
        },
        isSupersededSince: (token)=>clearEpochRef.current !== token,
        currentToken: ()=>clearEpochRef.current
    });
    function getConversationId() {
        return conversationIdRef.current;
    }
    function isRegreetAllowed() {
        return isOpen && !conversation.view.hasUserMessage && !conversation.chat.isThinking && !conversation.chat.isUploading && conversation.chat.pendingAttachment == null;
    }
    function resetConversation() {
        clearEpochRef.current += 1;
        conversationIdRef.current = null;
        createInFlightRef.current = null;
    }
    function commitConversationId(conversationId, epochAtCall) {
        if (clearEpochRef.current === epochAtCall) conversationIdRef.current = conversationId;
        return conversationId;
    }
    function createFreshConversation(epochAtCall) {
        const keyAtCall = greetingScopeKey;
        return createConversation().then((param)=>{
            let { conversationId, message } = param;
            const committed = commitConversationId(conversationId, epochAtCall);
            if (clearEpochRef.current === epochAtCall) {
                conversation.appendMessage(message);
                markGreeted(keyAtCall);
            }
            return committed;
        });
    }
    function recoverRecentOrCreate(epochAtCall) {
        return restoreMostRecent().then((restored)=>{
            if (restored == null) return createFreshConversation(epochAtCall);
            const committed = commitConversationId(restored.conversation.conversationId, epochAtCall);
            if (clearEpochRef.current === epochAtCall) {
                // WHY: replaying a restored thread pushes its own scope onto the table, moving the key — an automatic re-greet there would delete the thread the seller came back to.
                forgetGreeting();
                conversation.hydrate(restored.conversation.messages);
                if (restored.scope != null) onScopeRestored === null || onScopeRestored === void 0 ? void 0 : onScopeRestored(restored.scope);
            }
            return committed;
        });
    }
    function ensureConversationId(options) {
        if (conversationIdRef.current != null) return Promise.resolve(conversationIdRef.current);
        if (createInFlightRef.current != null) return createInFlightRef.current;
        const epochAtCall = clearEpochRef.current;
        const pending = ((options === null || options === void 0 ? void 0 : options.forceFresh) === true ? createFreshConversation(epochAtCall) : recoverRecentOrCreate(epochAtCall)).finally(()=>{
            if (clearEpochRef.current === epochAtCall) createInFlightRef.current = null;
        });
        createInFlightRef.current = pending;
        return pending;
    }
    const sendSellerTurn = (intent)=>{
        onSellerPrompted === null || onSellerPrompted === void 0 ? void 0 : onSellerPrompted();
        return conversation.send(intent);
    };
    const sendPrompt = (text, options)=>{
        lastInstructionRef.current = text;
        const attachment = conversation.chat.pendingAttachment;
        if (attachment != null) {
            conversation.clearPendingAttachment();
            const trimmed = text.trim();
            return sendSellerTurn(_object_spread({
                kind: 'fileUploaded',
                fileId: attachment.fileId,
                fileName: attachment.fileName
            }, trimmed.length > 0 ? {
                text: trimmed
            } : {}));
        }
        return sendSellerTurn(_object_spread({
            kind: 'prompt',
            text
        }, (options === null || options === void 0 ? void 0 : options.intentHint) != null ? {
            intentHint: options.intentHint
        } : {}));
    };
    const applyDecision = (messageId, selectedItemIds)=>sendSellerTurn({
            kind: 'apply',
            messageId,
            selectedItemIds
        });
    const takeSuggestion = (messageId, suggestion)=>{
        var _target_assistantContent;
        const target = conversation.chat.messages.find((message)=>message.id() === messageId);
        if ((target === null || target === void 0 ? void 0 : (_target_assistantContent = target.assistantContent()) === null || _target_assistantContent === void 0 ? void 0 : _target_assistantContent.type) === 'clarifying-question') {
            lastInstructionRef.current = suggestion.label;
            // WHY: answering a clarifying question must never consume a staged attachment, unlike sendPrompt
            return sendSellerTurn({
                kind: 'prompt',
                text: suggestion.label
            });
        }
        return sendSellerTurn({
            kind: 'listSelectClick',
            messageId,
            itemId: suggestion.id,
            label: suggestion.label
        });
    };
    const submitFeedback = async (messageId, rating, comment)=>{
        const id = conversationIdRef.current;
        if (id == null) return;
        const result = await handleRequest(sendFeedback({
            conversationId: id,
            messageId,
            rating,
            comment
        }));
        if (result.success) showSuccessToast(t.ai.drafting.feedback.thanks(), feedbackToastTestId);
    };
    const handlers = {
        onApply: applyDecision,
        onDismiss: (messageId)=>sendSellerTurn({
                kind: 'dismiss',
                messageId
            }),
        onSkip: (messageId)=>sendSellerTurn({
                kind: 'skip',
                messageId
            }),
        onSkipTheRest: (messageId)=>sendSellerTurn({
                kind: 'skipTheRest',
                messageId
            }),
        onListSelectClick: (messageId, itemId)=>applyDecision(messageId, [
                itemId
            ]),
        // WHY the backend-appended "Something else" chip is not an apply: no option was parked for it, so the
        // actor refuses it as an unknown id — which both errors at the seller and pages the team. It is a request
        // to restate, exactly as the single-draft pane treats it.
        onNumberedListClick: (messageId, option)=>{
            if (option.id !== SOMETHING_ELSE_OPTION_ID) return applyDecision(messageId, [
                option.id
            ]);
            // WHY stamped like any other prompt: the next turn's parser reads the last instruction for context.
            lastInstructionRef.current = option.label;
            return sendSellerTurn({
                kind: 'prompt',
                text: option.label
            });
        },
        onSubmitFeedback: submitFeedback,
        onDismissFeedback: ()=>undefined,
        onSuggestionClick: takeSuggestion,
        onUndo: conversation.undo,
        onFollowUpPrompt: sendPrompt,
        onStarterPromptClick: sendPrompt
    };
    return {
        conversation,
        handlers,
        sendPrompt,
        lastInstruction: ()=>lastInstructionRef.current
    };
};
